import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import {
  convertEstimateToInvoice,
  createBillingDocument,
  deleteBillingDocument,
  generateEstimateFromTimeEntries,
  generateInvoiceFromTimeEntries,
  getBillingDocument,
  issueBillingDocument,
  listBillingDocuments,
  listLineItems,
  listLineNatures,
  listBillingPayments,
  markBillingPaid,
  replaceBillingLines,
  updateBillingDocumentMeta,
  voidBillingDocument,
  type BillingFilters,
  type BillingLineDraft,
  type CreateBillingDocumentInput,
  type GenerateInvoiceInput,
  type UpdateBillingMetaInput,
} from '../../services/billing/billingService';
import {
  convertPolarDocument,
  figerPolarDocument,
  openPolarPdf,
  sendCimePolarDocument,
  syncBillingPayments,
} from '../../services/billing/polarBilling';
import type { SyncableDoc } from '../../lib/billing/syncPayments';
import type { BillingDocument } from '../../services/billing/billingService';

export const billingKeys = {
  all: ['billing'] as const,
  list: (filters: BillingFilters) => [...billingKeys.all, 'list', filters] as const,
  detail: (id: string) => [...billingKeys.all, 'detail', id] as const,
  lines: (id: string) => [...billingKeys.all, 'lines', id] as const,
  natures: (ids: string[]) => [...billingKeys.all, 'natures', ids] as const,
  payments: () => [...billingKeys.all, 'payments'] as const,
};

export function useBillingDocuments(filters: BillingFilters = {}) {
  return useQuery({
    ...STATIC,
    queryKey: billingKeys.list(filters),
    queryFn: () => listBillingDocuments(filters),
  });
}

/** Composition des pièces listées : nature et HT de chaque ligne. */
export function useBillingLineNatures(documentIds: string[]) {
  return useQuery({
    ...STATIC,
    queryKey: billingKeys.natures(documentIds),
    queryFn: () => listLineNatures(documentIds),
    enabled: documentIds.length > 0,
  });
}

/**
 * Recopie les règlements Polaris des pièces affichées, à l'ouverture puis toutes les
 * 5 minutes au plus. Clé hors du préfixe « billing » : rafraîchir les listes après la
 * copie ne relance pas la copie.
 */
export function useBillingPaymentsSync(docs: SyncableDoc[] | undefined) {
  const qc = useQueryClient();
  const targets = (docs ?? []).filter((d) => d.doc_type === 'invoice' && d.polar_invoice_id && d.number && d.status !== 'void');
  const ids = targets.map((d) => d.id).sort();
  return useQuery({
    queryKey: ['billing-payments-sync', ids],
    queryFn: async () => {
      const result = await syncBillingPayments(targets);
      if (result.synced > 0) await qc.invalidateQueries({ queryKey: billingKeys.all });
      return { ...result, at: new Date().toISOString() };
    },
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useBillingPayments() {
  return useQuery({
    ...STATIC,
    queryKey: billingKeys.payments(),
    queryFn: listBillingPayments,
  });
}

export function useBillingDocument(id: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: id ? billingKeys.detail(id) : ['billing', 'detail', 'noop'],
    queryFn: () => getBillingDocument(id!),
    enabled: !!id,
  });
}

export function useBillingLineItems(id: string | undefined) {
  return useQuery({
    ...STATIC,
    queryKey: id ? billingKeys.lines(id) : ['billing', 'lines', 'noop'],
    queryFn: () => listLineItems(id!),
    enabled: !!id,
  });
}

export function useGenerateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateInvoiceInput) => generateInvoiceFromTimeEntries(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
      qc.invalidateQueries({ queryKey: ['time_entries'] });
    },
  });
}

export function useGenerateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateInvoiceInput) => generateEstimateFromTimeEntries(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useCreateBillingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBillingDocumentInput) => createBillingDocument(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useIssueBillingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => issueBillingDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useMarkBillingPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paid_at, payment_method }: { id: string; paid_at?: string; payment_method?: string }) =>
      markBillingPaid(id, paid_at, payment_method),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useVoidBillingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => voidBillingDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
      qc.invalidateQueries({ queryKey: ['time_entries'] });
    },
  });
}

export function useDeleteBillingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBillingDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useReplaceBillingLines(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lines: BillingLineDraft[]) => replaceBillingLines(documentId, lines),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.lines(documentId) });
      qc.invalidateQueries({ queryKey: billingKeys.detail(documentId) });
      qc.invalidateQueries({ queryKey: ['time_entries'] });
    },
  });
}

export function useFigerPolarDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => figerPolarDocument(id, title),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: billingKeys.detail(doc.id) });
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useConvertPolarDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, serviceDate }: { id: string; serviceDate: string }) =>
      convertPolarDocument(id, serviceDate),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useOpenPolarPdf() {
  return useMutation({
    mutationFn: (doc: BillingDocument) => openPolarPdf(doc),
  });
}

export function useSendPolarDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sendCimePolarDocument(id),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: billingKeys.detail(doc.id) });
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useConvertEstimateToInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dueDate }: { id: string; dueDate?: string | null }) =>
      convertEstimateToInvoice(id, dueDate),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useUpdateBillingMeta(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBillingMetaInput) => updateBillingDocumentMeta(documentId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.detail(documentId) });
      qc.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}
